// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState } from 'react'
import { AlertTriangle, Wrench } from 'lucide-react'
import { guides } from './HowToGuide'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA', warningBg: '#FBF3E8',
}
const f = 'Georgia, serif'

function calcNextDue(lastDate, interval) {
  if (!lastDate || !interval) return null
  const d = new Date(lastDate)
  const i = interval.toLowerCase()
  if (i.includes('every use') || i.includes('each use')) return null
  if (i.includes('month') && i.includes('3')) d.setDate(d.getDate() + 90)
  else if (i.includes('month') && i.includes('6')) d.setDate(d.getDate() + 180)
  else if (i.includes('month')) d.setDate(d.getDate() + 30)
  else if (i.includes('90')) d.setDate(d.getDate() + 90)
  else if (i.includes('2 year')) d.setDate(d.getDate() + 730)
  else if (i.includes('year') || i.includes('annual')) d.setDate(d.getDate() + 365)
  else if (i.includes('180')) d.setDate(d.getDate() + 180)
  else if (i.includes('week')) d.setDate(d.getDate() + 7)
  else return null
  return d.toISOString().split('T')[0]
}

function getDueStatus(nextDue) {
  if (!nextDue) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((new Date(nextDue) - today) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'overdue'
  if (diffDays <= 30) return 'soon'
  return 'good'
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return dateStr }
}

const typeColors = {
  diy:        { bg: '#F2EDE4', text: '#9A7B68', label: 'DIY' },
  contractor: { bg: '#EBF5EF', text: '#2E6B4A', label: 'Contractor' },
  auto_order: { bg: '#FBF3E8', text: '#B8721F', label: 'Order Parts' },
}
const difficultyColors = {
  'Easy':     { bg: '#EBF5EF', text: '#2E6B4A' },
  'Moderate': { bg: '#FBF3E8', text: '#B8721F' },
  'Advanced': { bg: '#FDECEA', text: '#B03020' },
}

const inp = {
  width: '100%', background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: '8px', padding: '10px 14px', fontSize: '14px',
  fontFamily: f, color: C.brown, boxSizing: 'border-box', outline: 'none',
  marginBottom: '14px',
}
const lbl = {
  fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
  textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif',
  display: 'block', marginBottom: '5px',
}
const ghostBtn = {
  background: 'transparent', border: `1px solid ${C.border}`,
  borderRadius: '12px', padding: '13px', fontSize: '13px',
  color: C.muted, cursor: 'pointer', fontFamily: f,
  letterSpacing: '1px', textTransform: 'uppercase',
}
const primaryBtn = {
  background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
  border: 'none', borderRadius: '12px', padding: '15px',
  fontSize: '14px', color: C.card, fontWeight: '700',
  cursor: 'pointer', fontFamily: f, letterSpacing: '1px', textTransform: 'uppercase',
}

// Props:
//   task          — { task, interval, type, partCost }
//   applianceName — display name of the appliance or system
//   applianceType — slug (e.g. 'hvac', 'septic') for guide key lookup
//   completedTasks — { [taskKey]: { date } }  (optional, appliances only)
//   taskDates      — { [taskName|taskKey]: { lastCompleted, nextDue, interval } }  (optional)
//   onComplete({ taskKey, date, cost, provider, note }) — async, parent handles save + close
//   onMarkIncomplete() — optional, parent handles undo + close
//   onClose
export default function TaskDetailSheet({
  task,
  applianceName,
  applianceType,
  completedTasks = {},
  taskDates = {},
  onComplete,
  onMarkIncomplete,
  onClose,
}) {
  const today = new Date().toISOString().split('T')[0]
  const [mode, setMode] = useState('detail')
  const [completionDate, setCompletionDate] = useState(today)
  const [costAmount, setCostAmount] = useState('')
  const [costProvider, setCostProvider] = useState('')
  const [costNote, setCostNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const taskKey = `${task.task}_${task.interval}`
  const isChecked = !!completedTasks[taskKey]
  // taskDates can be keyed by task name (Dashboard/SystemDetail) or taskKey (ApplianceDetail)
  const td = taskDates[task.task] || taskDates[taskKey]
  const nextDue = td?.nextDue
  const lastCompleted = td?.lastCompleted || completedTasks[taskKey]?.date
  const hasRecord = isChecked || !!lastCompleted
  const status = getDueStatus(nextDue)

  const guideKey = `${applianceType}_${task.task.toLowerCase().replace(/\s+/g, '_')}`
  const guide = guides[guideKey]
  const tc = typeColors[task.type] || typeColors.diy
  const dc = guide?.difficulty ? difficultyColors[guide.difficulty] : null

  const nextDuePreview = completionDate && task.interval
    ? calcNextDue(completionDate, task.interval) : null

  const handleSave = async () => {
    setSubmitting(true)
    const date = completionDate || today
    const cost = costAmount ? parseFloat(costAmount) : null
    await onComplete({ taskKey, date, cost, provider: costProvider || null, note: costNote || null })
    // parent's onComplete is responsible for closing (setSelectedTask(null))
    setSubmitting(false)
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ width: '100%', maxWidth: '680px', maxHeight: '90vh', background: C.card, borderRadius: '20px 20px 0 0', border: `1px solid ${C.border}`, borderBottom: 'none', boxShadow: '0 -8px 40px rgba(44,29,20,0.2)', display: 'flex', flexDirection: 'column', fontFamily: f }}>

        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '6px', flexShrink: 0 }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: C.border }} />
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>

          {mode === 'detail' ? (
            <>
              {/* Header */}
              <div style={{ padding: '4px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, paddingRight: '12px' }}>
                  <div style={{ fontSize: '21px', fontWeight: '700', color: C.brown, lineHeight: 1.2 }}>{task.task}</div>
                  <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>{applianceName}</div>
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>

              <div style={{ padding: '14px 24px 32px' }}>

                {/* Meta badges: type, interval, difficulty */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: tc.bg, color: tc.text, border: `1px solid ${tc.text}33`, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>{tc.label}</span>
                  <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: C.bg, color: C.muted, border: `1px solid ${C.border}`, fontFamily: 'Calibri, sans-serif' }}>{task.interval}</span>
                  {dc && <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: dc.bg, color: dc.text, border: `1px solid ${dc.text}33`, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>{guide.difficulty}</span>}
                </div>

                {/* Completion status banner */}
                {hasRecord && (
                  <div style={{ background: C.successBg, border: `1px solid ${C.success}33`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '13px', color: C.success, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>
                        ✓ Completed{lastCompleted ? ` ${formatDate(lastCompleted)}` : ''}
                      </div>
                      {nextDue && (
                        <div style={{ fontSize: '11px', fontFamily: 'Calibri, sans-serif', marginTop: '2px', fontWeight: status !== 'good' ? '600' : '400', color: status === 'overdue' ? C.danger : status === 'soon' ? C.amber : C.muted }}>
                          Next due: {formatDate(nextDue)}{status === 'overdue' ? ' — OVERDUE' : status === 'soon' ? ' — due soon' : ''}
                        </div>
                      )}
                    </div>
                    {onMarkIncomplete && (
                      <button
                        onClick={onMarkIncomplete}
                        style={{ background: 'transparent', border: `1px solid ${C.muted}44`, borderRadius: '8px', padding: '5px 10px', fontSize: '10px', color: C.muted, cursor: 'pointer', fontFamily: 'Calibri, sans-serif', whiteSpace: 'nowrap', flexShrink: 0 }}
                      >Mark Incomplete</button>
                    )}
                  </div>
                )}

                {/* Not yet done: show due date urgency */}
                {!hasRecord && nextDue && (
                  <div style={{ marginBottom: '14px' }}>
                    <span style={{ fontSize: '12px', fontFamily: 'Calibri, sans-serif', fontWeight: '600', color: status === 'overdue' ? C.danger : status === 'soon' ? C.amber : C.muted }}>
                      {status === 'overdue' ? '⚠ Overdue since ' : status === 'soon' ? '⏰ Due soon — ' : 'Next due: '}{formatDate(nextDue)}
                    </span>
                  </div>
                )}

                {/* Part cost estimate */}
                {task.partCost && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', background: C.warningBg, border: `1px solid ${C.amber}33`, borderRadius: '8px', padding: '6px 12px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>~{task.partCost} in parts</span>
                  </div>
                )}

                {/* ── Guide content ── */}
                {guide ? (
                  <>
                    {guide.time && (
                      <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '14px' }}>
                        Estimated time: <strong style={{ color: C.brown }}>{guide.time}</strong>
                      </div>
                    )}

                    {guide.warning && (
                      <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <AlertTriangle size={14} strokeWidth={1.5} color={C.danger} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ fontSize: '13px', color: C.danger, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                          <strong>Safety note:</strong> {guide.warning}
                        </div>
                      </div>
                    )}

                    {guide.contractorNote && (
                      <div style={{ background: C.successBg, border: `1px solid ${C.success}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <Wrench size={14} strokeWidth={1.5} color={C.success} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ fontSize: '13px', color: C.success, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                          <strong>Contractor note:</strong> {guide.contractorNote}
                        </div>
                      </div>
                    )}

                    {guide.tools?.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', fontWeight: '700', marginBottom: '8px' }}>What You Need</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {guide.tools.map((tool, i) => (
                            <span key={i} style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '20px', background: C.bg, border: `1px solid ${C.border}`, color: C.brown, fontFamily: 'Calibri, sans-serif' }}>{tool}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {guide.steps?.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', fontWeight: '700', marginBottom: '12px' }}>Steps</div>
                        {guide.steps.map((step, i) => (
                          <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-start' }}>
                            <div style={{ width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, color: C.card, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', fontFamily: 'Calibri, sans-serif' }}>{i + 1}</div>
                            <div style={{ fontSize: '14px', color: C.brown, lineHeight: 1.7, fontFamily: 'Calibri, sans-serif', paddingTop: '3px' }}>{step}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {guide.proTip && (
                      <div style={{ background: C.warningBg, border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '14px 16px', marginBottom: '8px' }}>
                        <div style={{ fontSize: '10px', color: C.amber, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', fontWeight: '700', marginBottom: '4px' }}>Pro Tip</div>
                        <div style={{ fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>{guide.proTip}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: 'italic', marginBottom: '8px' }}>
                    No step-by-step guide available for this task yet.
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {!isChecked && onComplete && (
                    <button onClick={() => setMode('complete')} style={primaryBtn}>
                      Mark Complete
                    </button>
                  )}
                  {isChecked && onComplete && (
                    <button onClick={() => setMode('complete')} style={{ ...ghostBtn, borderColor: `${C.amber}66`, color: C.amber }}>
                      Log Again
                    </button>
                  )}
                  <button onClick={onClose} style={ghostBtn}>Cancel</button>
                </div>
              </div>
            </>
          ) : (
            /* ── Mark Complete sub-form ── */
            <div style={{ padding: '4px 24px 32px' }}>
              <div style={{ fontSize: '19px', fontWeight: '700', color: C.brown, marginBottom: '4px' }}>Mark Complete</div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '24px' }}>{task.task} · {applianceName}</div>

              <label style={lbl}>Date Completed</label>
              <input
                type="date"
                value={completionDate}
                max={today}
                onChange={e => setCompletionDate(e.target.value)}
                style={inp}
              />
              {nextDuePreview && (
                <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '-10px', marginBottom: '14px' }}>
                  Next due: {formatDate(nextDuePreview)}
                </div>
              )}

              <label style={lbl}>Cost (optional)</label>
              <input
                type="number"
                placeholder="e.g. 45"
                value={costAmount}
                onChange={e => setCostAmount(e.target.value)}
                style={inp}
              />

              <label style={lbl}>Service Provider (optional)</label>
              <input
                type="text"
                placeholder="e.g. HVAC Pro Services, Self / DIY"
                value={costProvider}
                onChange={e => setCostProvider(e.target.value)}
                style={inp}
              />

              <label style={lbl}>Note (optional)</label>
              <input
                type="text"
                placeholder="e.g. Used Nordic Pure filters this time"
                value={costNote}
                onChange={e => setCostNote(e.target.value)}
                style={{ ...inp, marginBottom: '24px' }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={handleSave}
                  disabled={submitting}
                  style={{ ...primaryBtn, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
                >{submitting ? 'Saving...' : '✓ Save & Complete Task'}</button>
                <button onClick={() => setMode('detail')} style={ghostBtn}>Back</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
